import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaCloudUploadAlt } from 'react-icons/fa';
import DynamicSelect from './DynamicSelect';

const API_BASE = 'https://api.muktihospital.com/api/medicine/add';
const API_KEY = 'caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079';

const AddMedicineModal = ({ isOpen, onClose, categories, groups, companies, units, onSave }) => {
  const [formData, setFormData] = useState({
    medicineName: '',
    medicineCategory: '',
    medicineCompany: '',
    medicineGroup: '',
    unit: '',
    rackNumber: '',
    note: '',
    medicinePhoto: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  // Helper to map API data into select options with fallback
  const mapToOptions = (items, labelKey, valueKey) => {
    if (!Array.isArray(items)) return [{ value: '', label: 'Select' }];
    return [
      { value: '', label: 'Select' },
      ...items.map(item => ({
        label: item[labelKey] || item.title || Object.values(item)[0] || 'Unknown',
        value: item[valueKey] || item[labelKey] || item.title || Object.values(item)[0] || 'Unknown',
      })),
    ];
  };

  // Derive options from props
  const categoryOptions = mapToOptions(categories, 'name', 'name');
  const groupOptions = mapToOptions(groups, 'groupName', 'groupName');
  const companyOptions = mapToOptions(companies, 'companyName', 'companyName');
  const unitOptions = mapToOptions(units, 'name', 'name');


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name) => (selected) => {
    setFormData((prev) => ({ ...prev, [name]: selected ? selected.value : '' }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setFormData((prev) => ({ ...prev, medicinePhoto: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { medicineName, medicineCategory, medicineCompany, medicineGroup, unit } = formData;

    // Validate required fields
    if (!medicineName || !medicineCategory || !medicineCompany || !medicineGroup || !unit) {
      toast.error('Please fill in all required fields.');
      return;
    }

    setIsLoading(true);
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== '') payload.append(key, value);
      });

      const response = await axios.post(API_BASE, payload, {
        headers: {
          'x-api-key': API_KEY,
        },
      });
      setFormData({
        medicineName: '',
        medicineCategory: '',
        medicineCompany: '',
        medicineGroup: '',
        unit: '',
        rackNumber: '',
        note: '',
        medicinePhoto: null,
      });

      if (response.data && response.data.medicine) {
        onSave(response.data.medicine);
      } else {
        onSave();
      }

      onClose();
    } catch (error) {
      console.error('Add medicine error:', error);
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'An error occurred. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Check if options are loaded
  const isDataLoading = !categories || !groups || !companies || !units;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-full sm:max-w-[80%] md:max-w-[700px] lg:max-w-screen-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700">Add Medicine Details</h2>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Medicine Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medicine Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="medicineName"
                value={formData.medicineName}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md outline-none focus:ring-0 text-sm"
                placeholder="Medicine Name"
              />
            </div>
            {/* Category */}
            <div className="z-10">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medicine Category <span className="text-red-500">*</span>
              </label>
              <DynamicSelect
                options={categoryOptions}
                value={categoryOptions.find((o) => o.value === formData.medicineCategory)}
                onChange={handleSelectChange('medicineCategory')}
                isSearchable
                placeholder="Select"
                isDisabled={isDataLoading}
              />
            </div>
            {/* Company */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medicine Company <span className="text-red-500">*</span>
              </label>
              <DynamicSelect
                options={companyOptions}
                value={companyOptions.find((o) => o.value === formData.medicineCompany)}
                onChange={handleSelectChange('medicineCompany')}
                isSearchable
                placeholder="Select"
                isDisabled={isDataLoading}
              />
            </div>
            {/* Group */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medicine Group <span className="text-red-500">*</span>
              </label>
              <DynamicSelect
                options={groupOptions}
                value={groupOptions.find((o) => o.value === formData.medicineGroup)}
                onChange={handleSelectChange('medicineGroup')}
                isSearchable
                placeholder="Select"
                isDisabled={isDataLoading}
              />
            </div>
            {/* Unit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit <span className="text-red-500">*</span>
              </label>
              <DynamicSelect
                options={unitOptions}
                value={unitOptions.find((o) => o.value === formData.unit)}
                onChange={handleSelectChange('unit')}
                isSearchable
                placeholder="Select"
                isDisabled={isDataLoading}
              />
            </div>
            {/* Rack Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rack Number</label>
              <input
                type="text"
                name="rackNumber"
                value={formData.rackNumber}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md outline-none focus:ring-0 text-sm"
                placeholder="Rack Number"
              />
            </div>
            {/* Photo Upload */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Medicine Photo (JPG, JPEG, PNG)</label>
              <div className="relative">
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="w-full p-2 border rounded-md outline-none focus:ring-0 text-sm opacity-0 absolute"
                />
                <div className="w-full p-2 border rounded-md text-sm text-gray-500 flex items-center justify-between">
                  {formData.medicinePhoto ? formData.medicinePhoto.name : 'Drop a file here or click'}
                  <FaCloudUploadAlt className="text-gray-400" size={20} />
                </div>
              </div>
            </div>
            {/* Note */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
              <textarea
                name="note"
                value={formData.note}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md outline-none focus:ring-0 text-sm"
                rows="3"
                placeholder="Note"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || isDataLoading}
              className={`bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md transition-all duration-200 ${
                isLoading || isDataLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-M-primary-color'
              }`}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMedicineModal;